import { Col, Container, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { FormCheckbox, FormInput, FormSelect, FormTextArea } from '@components/Common/AwpFormInputs';
import React from 'react';
import { ENTITY_TITLES, FORM_MODE, REST_API } from '@awp/constants';
import * as Yup from 'yup';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import { GetJson } from '@awp/helpers/fetchReact';
import AvalanchePathImagesList from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesList';
import { CrudService, EntityProps } from '@awp/shared/CrudService';
import { useSearchParams } from 'react-router-dom';
import { EventKey } from 'react-bootstrap/esm/types';
import AvalanchePathImagesGrid from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesGrid';
import { AvalanchePathImagesContextProvider } from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesContext';
import { AVALANCHE_PATH_IMAGES_PARAMS } from '@components/AvalanchePathsAdmin/Images/AvalanchePathImagesService';

const TERRAIN_CHARACTERISTICS = {
  vertFall: null,
  startingZoneArea: null,
  runOutArea: null,
  roadWidth: null,
  slopeLengthToRoad: null,
  slopeAngleToRoad: null,
  mainAspect: null,
  lengthOfHighwayAffected: null,
};
const ELEVATIONS = {
  startingZoneTop: null,
  startingZoneBottom: null,
  runoutZoneTop: null,
  runoutZoneBottom: null,
};
const INCLINES = {
  startingZone: null,
  track: null,
  runoutZone: null,
};
const PATH_NOTES = {
  startingZoneDescription: null,
  trackDescription: null,
  runoutZoneDescription: null,
  technicianDescriptionOfPathCharacteristics: null,
};

export const AVALANCHE_PATHS_DEFAULT_DTO = {
  program: null,
  value: null,
  Name: null,
  PathNumber: null,
  locationNote: null,
  pathCode: null,
  metaFields: {
    terrainCharacteristics: TERRAIN_CHARACTERISTICS,
    elevations: ELEVATIONS,
    inclines: INCLINES,
    pathNotes: PATH_NOTES,
  },
  status: null,
  Code: null,
};

export const AVALANCHE_PATH_PARAMS = {
  TAB_KEY: 'tabKey',
  LIST_TYPE: 'listType',
};

export const AVALANCHE_PATH_IMAGES_LIST_TYPE = {
  TABLE: 'table',
  GRID: 'grid',
};

export class AvalanchePathsService extends CrudService {
  public searchParams;
  public setSearchParams;

  constructor(mode: string) {
    const entityName = ENTITY_TITLES.AVALANCHE_PATH;
    const entityPluralName = ENTITY_TITLES.AVALANCHE_PATHS;

    super({ entityName, entityPluralName, mode });

    [this.searchParams, this.setSearchParams] = useSearchParams();
  }

  public setTabSearchParams = (setSearchParams: any, event?: any) => {
    setSearchParams((params: URLSearchParams) => {
      params.set(AVALANCHE_PATH_PARAMS.TAB_KEY, event);

      if (event === 'images' && !params.get(AVALANCHE_PATH_PARAMS.LIST_TYPE)) {
        params.set(AVALANCHE_PATH_PARAMS.LIST_TYPE, AVALANCHE_PATH_IMAGES_LIST_TYPE.GRID);
      }

      return params;
    });
  };

  updateAvalanchePathCode = (pathNumber: string, area: any) => {
    if (!pathNumber || !/\d{1,3}\.\d{1,2}/.test(pathNumber)) {
      return '';
    }

    try {
      const number = parseFloat(pathNumber) * 100;
      const paddedNumber = number.toFixed(0).padStart(5, '0');
      return `${area.code}${paddedNumber}`;
    } catch {
      return '';
    }
  };

  static loadAvalanchePath = async (entityId: any) => {
    const url = `${REST_API.AVALANCHE_PATH}${entityId}`;
    return GetJson(url).then((response) => response.json());
  };

  mapInitialValues(props: any) {
    const { entityData, avalanchePrograms, areaList, areaId } = props;
    const myAreaId = areaId ?? entityData.AreaId;
    const area = areaList.find((a: any) => a.value === myAreaId);

    return {
      ...entityData,
      program: avalanchePrograms.find((x: any) => x.value === area?.parentId),
      area: area ?? null,
      active: entityData.IsActive === 'Y',
      isDisabled: entityData.IsActive !== 'Y', // TODO: Check if needed
      Code: this.updateAvalanchePathCode(entityData.PathNumber, area),
    };
  }

  mapFormToDTO(props: any) {
    const { entityData, areaId } = props;

    return {
      AreaId: areaId ?? entityData?.area?.value,
      Name: entityData.Name,
      Code: entityData.Code,
      LocationNote: entityData.locationNote,
      PathNumber: entityData.PathNumber,
      IsActive: entityData.active ?? true,
    };
  }

  static getValidations() {
    return Yup.object({
      program: Yup.object()
        .nullable()
        .test('program', 'Program is a required field', (value, context: any) => {
          return (!isValueEmpty(value) && !isValueEmpty(value?.value)) || context?.originalValue?.length > 0;
        }),
      area: Yup.object()
        .nullable()
        .test('area', 'Area is a required field', (value, context: any) => {
          return (!isValueEmpty(value) && !isValueEmpty(value?.value)) || context?.originalValue?.length > 0;
        }),
      Name: Yup.string()
        .nullable()
        .max(50, 'Maximum 50 characters are allowed')
        .required('Avalanche Path Name is a required field'),
      PathNumber: Yup.string()
        .nullable()
        .matches(/^\d{1,3}\.\d{1,2}$/, 'Must be in the format 3 digits left and 2 digits right of decimal')
        .required('Avalanche Path Number is a required field'),
      locationNotes: Yup.string().nullable().max(250, 'Maximum 250 characters are allowed'),
      Code: Yup.string().nullable(),
      vertFall: Yup.string().nullable().max(6, 'Max 6 characters'),
      startingZoneArea: Yup.string().nullable().max(4, 'Max 4 characters'),
      runOutArea: Yup.string().nullable().max(4, 'Max 4 characters'),
      roadWidth: Yup.string().nullable().max(7, 'Max 7 characters'),
      slopeLengthToRoad: Yup.string().nullable().max(10, 'Max 10 characters'),
      slopeAngleToRoad: Yup.string().nullable().max(4, 'Max 4 characters'),
      mainAspect: Yup.string().nullable().max(3, 'Max 3 characters'),
      lengthOfHighwayAffected: Yup.string().nullable().max(9, 'Max 9 characters'),
      startingZoneTop: Yup.string().nullable().max(4, 'Max 4 characters'),
      startingZoneBottom: Yup.string().nullable().max(4, 'Max 4 characters'),
      runoutZoneTop: Yup.string().nullable().max(4, 'Max 4 characters'),
      runOutZoneBottom: Yup.string().nullable().max(4, 'Max 4 characters'),
      startingZone: Yup.string().nullable().max(3, 'Max 3 characters'),
      track: Yup.string().nullable().max(3, 'Max 3 characters'),
      runoutZone: Yup.string().nullable().max(3, 'Max 3 characters'),
      startingZoneDescription: Yup.string().nullable().max(1000, 'Max 1000 characters'),
      trackDescription: Yup.string().nullable().max(1000, 'Max 1000 characters'),
      runoutZoneDescription: Yup.string().nullable().max(1000, 'Max 1000 characters'),
      technicianDescriptionOfPathCharacteristics: Yup.string().nullable().max(1000, 'Max 1000 characters'),
    });
  }

  public getFields(args: any) {
    const {
      readOnly,
      avalanchePrograms,
      areaList,
      entityData,
      props,
      onNameInput,
      onPathNumberInput,
      areaOnChange,
      programOnChange,
      loading,
      mode,
      onTabSelect,
      tabKey,
    } = args;

    return (
      <>
        <Row>
          <Col sm={6}>
            <FormSelect
              className="avalanche-select"
              readOnly={readOnly || mode === FORM_MODE.EDIT}
              name="program"
              id="program"
              label="Program"
              useMargin={true}
              postChange={(e: any) => {
                programOnChange?.(e, props.setFieldValue);
              }}
              options={avalanchePrograms}
              isMulti={false}
              textValue={undefined}
              instruction={undefined}
              value=""
            />
          </Col>
          <Col sm={6}>
            <FormSelect
              className="avalanche-select"
              readOnly={readOnly || mode === FORM_MODE.EDIT}
              name="area"
              id="area"
              label="Avalanche Area"
              useMargin={true}
              postChange={(e: any) => {
                areaOnChange?.(e, props.setFieldValue);
              }}
              options={areaList}
              isMulti={false}
              textValue={undefined}
              instruction={undefined}
              value=""
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6} className="mb-4">
            <FormInput
              label="Avalanche Path Name"
              instruction="(50 Characters max)"
              readOnly={readOnly || entityData.IsProtected === 'Y'}
              name="Name"
              id="Name"
              onChange={(e: any) => onNameInput?.(e.target.value, props.setFieldValue)}
            />
          </Col>
          <Col sm={6} className="mb-4">
            <FormInput
              label="Avalanche Path Number"
              readOnly={readOnly || mode !== FORM_MODE.CREATE || entityData.IsProtected === 'Y'}
              onChange={(e: any) => onPathNumberInput?.(e.target.value, props.setFieldValue)}
              name="PathNumber"
              id="PathNumber"
              instruction={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6} className="mb-4">
            <FormInput
              label="Avalanche Path Code"
              readOnly={true}
              name="Code"
              id="Code"
              instruction={undefined}
              onChange={undefined}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={8} className="mb-4">
            <FormTextArea
              readOnly={true}
              label="Avalanche Path Location Notes"
              instruction="(250 Characters max)"
              id="locationNotes"
              name="locationNotes"
              rows={3}
              value=""
            />
          </Col>
        </Row>
        {mode !== FORM_MODE.CREATE && (
          <Row>
            <Col sm={6} className="mb-4">
              <FormCheckbox
                useMargin={true}
                readOnly={readOnly}
                id="active"
                name="active"
                label="Active"
                value={entityData.status}
                onCheck={undefined}
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col sm={12} className="mb-4">
            {entityData?.metaFields &&
              this.getTabs({
                loading,
                readOnly,
                onTabSelect,
                defaultDto: entityData?.metaFields,
                tabKey,
                mode,
              })}
          </Col>
        </Row>
      </>
    );
  }

  public getTabs(args: any) {
    const { loading, defaultDto, readOnly, onTabSelect, mode } = args;
    const avalanchePathDataReadOnly = true;

    const listType: string | null = this.searchParams.get(AVALANCHE_PATH_PARAMS.LIST_TYPE);
    const tabKey: EventKey | undefined = this.searchParams.get(AVALANCHE_PATH_PARAMS.TAB_KEY) ?? undefined;

    return (
      <>
        {loading ? (
          <Container>
            <Spinner animation="border" />
          </Container>
        ) : (
          <Tabs
            defaultActiveKey="terrainCharacteristics"
            id="path-data-tabs"
            className="mb-3"
            onSelect={(evt) => {
              onTabSelect?.(evt);
            }}
            activeKey={tabKey}
          >
            {Object.keys(defaultDto).map((fieldKey) => (
              <Tab
                eventKey={fieldKey}
                key={fieldKey}
                title={fieldKey.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase())}
              >
                <Row>
                  {Object.keys(defaultDto[fieldKey]).map((key, index) => (
                    <Col sm={6} key={key} className="mb-3">
                      <FormInput
                        label={key.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase())}
                        name={`${key}`}
                        id={`${key}`}
                        readOnly={avalanchePathDataReadOnly}
                        instruction={undefined}
                        onChange={undefined}
                      />
                    </Col>
                  ))}
                </Row>
              </Tab>
            ))}
            {mode !== FORM_MODE.CREATE && (
              <Tab eventKey="images" key="images" title="Images">
                <Row>
                  <Col>
                    <i
                      className={`fa fa-lg ${
                        listType === AVALANCHE_PATH_IMAGES_LIST_TYPE.TABLE ? 'fa-th' : 'fa-list'
                      } float-right awp-img-btn`}
                      title={listType === AVALANCHE_PATH_IMAGES_LIST_TYPE.TABLE ? 'Grid' : 'List'}
                      onClick={() => {
                        this.setSearchParams((params: URLSearchParams) => {
                          if (listType === AVALANCHE_PATH_IMAGES_LIST_TYPE.TABLE) {
                            params.set(AVALANCHE_PATH_PARAMS.LIST_TYPE, AVALANCHE_PATH_IMAGES_LIST_TYPE.GRID);
                          } else {
                            params.set(AVALANCHE_PATH_PARAMS.LIST_TYPE, AVALANCHE_PATH_IMAGES_LIST_TYPE.TABLE);
                            params.delete(AVALANCHE_PATH_IMAGES_PARAMS.SORT_BY);
                          }

                          return params;
                        });
                      }}
                    />
                  </Col>
                </Row>
                {tabKey === 'images' && (
                  <AvalanchePathImagesContextProvider>
                    {listType === AVALANCHE_PATH_IMAGES_LIST_TYPE.TABLE ? (
                      <AvalanchePathImagesList readOnly={readOnly} mode={mode} />
                    ) : (
                      <AvalanchePathImagesGrid readOnly={readOnly} mode={mode} />
                    )}
                  </AvalanchePathImagesContextProvider>
                )}
              </Tab>
            )}
          </Tabs>
        )}
      </>
    );
  }

  getValidations(props?: any): any {
    /* Do Nothing */
  }

  getActionMenu(event: any): React.ReactElement {
    return <></>;
  }

  getColumns(props: any): any {
    /* Do Nothing */
  }

  cleanFormValues(props: EntityProps): any {
    /* Do Nothing */
  }
}
